import { Badge, Flex, Text, Tooltip } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { SupplierListItem } from '@texas/api/endpoints/suppliersApi';
import { TFunction } from 'i18next';
import { ArchivedTooltip } from '../shared/tooltip/ArchivedTooltip';
import { SupplierTypeBadge } from './SupplierTypeBadge';
import { SupplierNameCell } from './SupplierNameCell';
import { SupplierSyncCoverage } from './SupplierSyncCoverage';
import { TagWithTooltip } from '../shared/TagWithTooltip';

export const suppliersColumns = (t: TFunction) => {
  const columnHelper = createColumnHelper<SupplierListItem>();
  const columns = [
    columnHelper.accessor('name', {
      cell: (info) => <SupplierNameCell supplier={info.row.original} />,
      header: t('general.name'),
      size: 300,
      minSize: 240,
    }),
    columnHelper.accessor('isInternal', {
      cell: (info) => <SupplierTypeBadge isInternal={info.getValue()} />,
      header: t('suppliers.type'),
      size: 110,
      minSize: 100,
    }),
    columnHelper.accessor('isItlSupplier', {
      cell: (info) =>
        info.getValue() ? (
          <Badge variant="entity-directory-reference">{t('general.yes')}</Badge>
        ) : null,
      header: t('suppliers.isItlSupplier'),
      size: 120,
      minSize: 110,
    }),
    columnHelper.accessor('supplierNumbers', {
      cell: (info) => {
        const supplierNumbers = info.getValue();
        if (!supplierNumbers) return null;

        return (
          <Flex gap={1} maxW="100%" minW={0} overflow="hidden" wrap="wrap">
            {supplierNumbers.map((supplierNumber) => (
              <Tooltip
                key={supplierNumber.branchIdentifier}
                label={`${supplierNumber.branchIdentifier}: ${supplierNumber.number}`}
              >
                <Badge
                  display="block"
                  maxW="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  variant="entity-directory-reference"
                  whiteSpace="nowrap"
                >
                  {supplierNumber.number}
                </Badge>
              </Tooltip>
            ))}
          </Flex>
        );
      },
      header: t('suppliers.supplierNumber'),
      enableSorting: false,
      size: 200,
      minSize: 160,
    }),
    columnHelper.accessor('tags', {
      cell: (info) => (
        <Flex gap={1} maxW="100%" minW={0} wrap="wrap">
          {info.getValue().map((tag) => (
            <TagWithTooltip key={tag.id} tag={tag} />
          ))}
        </Flex>
      ),
      header: t('general.tags'),
      enableSorting: false,
      size: 220,
      minSize: 160,
    }),
    columnHelper.accessor('articleCount', {
      cell: (info) => (
        <Flex align="baseline" gap={1}>
          <Text fontWeight="semibold">
            {info.row.original.syncedArticleCount}
          </Text>
          <Text color="entityDirectory.mutedText">/ {info.getValue()}</Text>
        </Flex>
      ),
      header: `${t('suppliers.synced')} / ${t('general.total')}`,
      size: 140,
      minSize: 125,
    }),
    columnHelper.accessor('id', {
      cell: (info) => (
        <SupplierSyncCoverage
          synced={info.row.original.syncedArticleCount}
          total={info.row.original.articleCount}
        />
      ),
      header: t('suppliers.syncCoverage'),
      enableSorting: false,
      size: 180,
      minSize: 160,
    }),
    columnHelper.accessor('lastArticleCreated', {
      cell: (info) => {
        const value = info.getValue();
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
      },
      header: t('suppliers.lastCreatedArticle'),
      size: 190,
      minSize: 160,
    }),
    columnHelper.accessor('archived', {
      cell: (info) => (
        <Flex justifyContent="end" alignItems="center">
          {info.row.original.archived && (
            <ArchivedTooltip archived={info.row.original.archived} />
          )}
        </Flex>
      ),
      header: '',
      enableSorting: false,
      size: 48,
      maxSize: 48,
    }),
  ];

  return columns;
};
